import React, { memo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Map } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';

import {
  findCompanies,
  changeField,
  findTitles,
  clearSuggest,
  addContactPosition,
  closeValidationError,
  resetPosition,
} from '../../actions/contact/contactExecutive';
import { reloadContactDetail } from '../../actions/contactDetail';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

const getFields = () => ({ id: 'newPosition.entityId', value: 'newPosition.suggestCompany' });

const handleGetValue = suggestion => suggestion?.value ?? '';

const AddPositionPopupContainer = ({
  children,
  popup,
  contact,
  executive,
  inputErrors,
  findCompanies,
  findTitles,
  changeField,
  addContactPosition,
  reloadContactDetail,
  closeValidationError,
  resetPosition,
}) => {
  const context = usePopupContext();

  useEffect(() => {
    const {
      props: { newTargetId, newTargetIdLegalName },
    } = popup.toJS();

    if (newTargetId > 0 && newTargetIdLegalName) {
      handleSelectCompany({ target: { name: '' } }, { suggestion: { id: newTargetId, text: newTargetIdLegalName } });
    }

    return () => {
      resetPosition();
    };
  }, []);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleClose = useCallback(() => {
    context.closePopup();
  }, [context]);

  const handleChange = useCallback(
    event => {
      const { name: preName, value } = event.target;
      const name = /^newPosition\./.test(preName) ? preName : `newPosition.${preName}`;

      changeField({ name, value });
    },
    [changeField],
  );

  const handleCreate = useCallback(() => {
    const id = contact.get('id', 0);
    const entityId = executive.getIn(['newPosition', 'entityId'], 0);
    const newPosition = executive.get('newPosition').toJS();

    if ('startYear' in newPosition) {
      newPosition.startYear = parseInt(newPosition.startYear, 10) || null;
    }
    Object.keys(newPosition).forEach(field => {
      if (newPosition[field] === null) delete newPosition[field];
    });

    addContactPosition({ ...newPosition, entityId, id }, () => {
      reloadContactDetail({ id }, handleClose);
    });
  }, [contact, executive, addContactPosition, reloadContactDetail, handleClose]);

  const handleFetch = useCallback(
    ({ value }) => {
      findCompanies({ filter: value });
    },
    [findCompanies],
  );

  const handleSelectCompany = useCallback(
    (event, { suggestion }) => {
      event.target.name = 'newPosition.suggestCompany';

      const { value, id } = getFields();

      changeField({ name: value, value: suggestion.text });
      changeField({ name: id, value: suggestion.id });
    },
    [changeField],
  );

  const handleUpdateCompany = useCallback(
    param => {
      if ('text' in param) {
        changeField({ name: 'newPosition.suggestCompany', value: param.text });
      }

      if ('suggestions' in param) {
        changeField({ name: 'suggests', value: param.suggestions });
      }
    },
    [changeField],
  );

  const handleCreateCompany = useCallback(() => {
    context.onClosePopup(() => {
      context.openPopup('AddCompanyTargetPopup', {
        fromPositionPopup: true,
        cb: popup.getIn(['props', 'cb']),
      });
    });
    context.closePopup();
  }, [context, popup]);

  const handleFetchTitles = useCallback(
    ({ value }) => {
      findTitles({ filter: value });
    },
    [findTitles],
  );

  const handleUpdateTitle = useCallback(
    param => {
      if ('text' in param) {
        changeField({ name: 'newPosition.title', value: param.text });
      }

      if ('suggestions' in param) {
        changeField({ name: 'suggests', value: param.suggestions });
      }
    },
    [changeField],
  );

  const handleSelectTitle = useCallback(
    (event, { suggestion }) => {
      changeField({ name: 'newPosition.title', value: suggestion.text });
    },
    [changeField],
  );

  const suggestCompany = {
    onAdd: handleCreateCompany,
    onFetch: handleFetch,
    onUpdate: handleUpdateCompany,
    getValue: handleGetValue,
    onSelect: handleSelectCompany,
    loading: executive.get('loadingExecCompanies'),
  };

  const suggestTitles = {
    onFetch: handleFetchTitles,
    onUpdate: handleUpdateTitle,
    getValue: handleGetValue,
    onSelect: handleSelectTitle,
    loading: executive.getIn(['newPosition', 'titleLoading']),
  };

  const position = executive.get('newPosition');

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.AddPositionPopup
          contact={contact}
          executive={executive}
          isValid={inputErrors && inputErrors.size === 0 && position.get('suggestCompany')}
          onChange={handleChange}
          onClose={handleClose}
          onCreate={handleCreate}
          position={position}
          suggestCompany={suggestCompany}
          suggestTitles={suggestTitles}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    inputErrors: state.contact.executive.get('newPositionInputErrors', Map()),
    contact: state.contact.info,
    executive: state.contact.executive,
  };
}

export { AddPositionPopupContainer };
export default connect(
  mapStateToProps,
  {
    findCompanies,
    findTitles,
    clearSuggest,
    addContactPosition,
    reloadContactDetail,
    changeField,
    closeValidationError,
    resetPosition,
  },
  mergeProps,
  connectOptions,
)(memo(AddPositionPopupContainer));
